import { forwardRef, useImperativeHandle } from 'react';
import { cn } from '@core/ui';
import { useAutoGrow } from './useAutoGrow';

export interface AutoGrowTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minRows?: number;
  className?: string;
  id?: string;
  'aria-label'?: string;
  onBlur?: () => void;
}

/**
 * Borderless auto-growing textarea — the description/notes field in create
 * dialogs and composers. Ghost placeholder, no box, no resize handle.
 * See docs/ux/14-forms.md.
 */
export const AutoGrowTextarea = forwardRef<HTMLTextAreaElement, AutoGrowTextareaProps>(
  (
    { value, onChange, placeholder, minRows = 2, className, id, 'aria-label': ariaLabel, onBlur },
    forwardedRef
  ) => {
    const ref = useAutoGrow(value);
    useImperativeHandle(forwardedRef, () => ref.current as HTMLTextAreaElement);

    return (
      <textarea
        ref={ref}
        id={id}
        rows={minRows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        placeholder={placeholder}
        autoCapitalize="off"
        aria-label={ariaLabel || placeholder}
        className={cn(
          'w-full resize-none overflow-hidden bg-transparent text-sm leading-relaxed',
          'text-foreground placeholder:text-muted-foreground/60 focus:outline-none',
          className
        )}
      />
    );
  }
);

AutoGrowTextarea.displayName = 'AutoGrowTextarea';
